import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import V4Tabs from './V4Tabs';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const DocumentsAssistantV4 = () => {
  const { getAuthHeaders } = useAuth();
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [history, setHistory] = useState([]);
  const [debug, setDebug] = useState(null);
  const [abortCtrl, setAbortCtrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recState, setRecState] = useState({ recording: false, supported: false });
  const [srState, setSrState] = useState({ listening: false, supported: false });
  const srRef = useRef(null);
  const liveTextRef = useRef('');
  const liveEndTimerRef = useRef(null);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [viewMode, setViewMode] = useState('beautified'); // 'beautified' | 'raw'

  const headers = useMemo(() => ({ ...getAuthHeaders(), 'Content-Type': 'application/json' }), [getAuthHeaders]);

  useEffect(() => {
    setRecState((s) => ({ ...s, supported: !!(navigator.mediaDevices && window.MediaRecorder) }));
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSrState((s) => ({ ...s, supported: !!SR }));
  }, []);

  const loadDocs = useCallback(async () => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset), q });
    const res = await fetch(`${API_BASE_URL}/api/v4/documents/list?${params.toString()}`, { headers: { ...getAuthHeaders() } });
    const data = await res.json();
    setDocs(Array.isArray(data.items) ? data.items : []);
    setTotal(data.total || 0);
  }, [getAuthHeaders, limit, offset, q]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const speakText = (text) => {
    try { window.speechSynthesis.cancel(); } catch {}
    if (!text) return;
    // Decide provider via settings endpoint (lightweight fetch)
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v4/documents/config/settings`, { headers: { ...getAuthHeaders() } });
        const data = await res.json();
        const provider = data?.config?.tts_provider || 'browser';
        if (provider === 'google') {
          const r2 = await fetch(`${API_BASE_URL}/api/v4/documents/assistant/tts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ text })
          });
          const j = await r2.json();
          if (j?.audio_b64 && j?.mime) {
            const src = `data:${j.mime};base64,${j.audio_b64}`;
            const audio = new Audio(src);
            setSpeaking(true);
            audio.onended = () => setSpeaking(false);
            audio.onerror = () => setSpeaking(false);
            audio.play().catch(() => setSpeaking(false));
            return;
          }
        }
      } catch {}
      // Fallback to browser TTS
      const plain = String(text)
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/`[^`]*`/g, ' ')
        .replace(/[#*_>\-\[\]()`]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (!plain) return;
      const utter = new SpeechSynthesisUtterance(plain);
      utter.rate = 1.0;
      utter.onstart = () => setSpeaking(true);
      utter.onend = () => setSpeaking(false);
      utter.onerror = () => setSpeaking(false);
      try { window.speechSynthesis.speak(utter); } catch { setSpeaking(false); }
    })();
  };
  const stopSpeaking = () => { try { window.speechSynthesis.cancel(); } catch {}; setSpeaking(false); };

  useEffect(() => {
    if (ttsEnabled && !loading && answer) {
      // Auto-read final answer after streaming completes
      speakText(answer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttsEnabled, loading]);

  const startLiveSTT = () => {
    if (!srState.supported || srState.listening) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    srRef.current = rec;
    rec.lang = 'en-US';
    rec.continuous = true;
    rec.interimResults = true;
    let finalText = '';
    liveTextRef.current = '';
    if (liveEndTimerRef.current) { try { clearTimeout(liveEndTimerRef.current); } catch {} liveEndTimerRef.current = null; }
    rec.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const transcript = e.results[i][0].transcript;
        if (e.results[i].isFinal) {
          finalText += transcript + ' ';
        } else {
          interim += transcript;
        }
      }
      const combined = (finalText + interim).trim();
      liveTextRef.current = combined;
      setQuestion(combined);
      if (liveEndTimerRef.current) { try { clearTimeout(liveEndTimerRef.current); } catch {} }
      liveEndTimerRef.current = setTimeout(() => { try { rec.stop(); } catch {} }, 1200);
    };
    rec.onerror = () => {
      try { rec.stop(); } catch {}
      setSrState({ listening: false, supported: srState.supported });
      if (liveEndTimerRef.current) { try { clearTimeout(liveEndTimerRef.current); } catch {} liveEndTimerRef.current = null; }
    };
    rec.onend = () => {
      setSrState({ listening: false, supported: srState.supported });
      if (liveEndTimerRef.current) { try { clearTimeout(liveEndTimerRef.current); } catch {} liveEndTimerRef.current = null; }
      const text = (liveTextRef.current || question || '').trim();
      if (text) {
        ask(text, { is_voice: true });
      }
    };
    try { rec.start(); setSrState({ listening: true, supported: srState.supported }); } catch {}
  };

  const stopLiveSTT = () => {
    try { srRef.current && srRef.current.stop(); } catch {}
    setSrState((s) => ({ ...s, listening: false }));
    if (liveEndTimerRef.current) { try { clearTimeout(liveEndTimerRef.current); } catch {} liveEndTimerRef.current = null; }
  };

  const startRecording = async () => {
    if (!recState.supported) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    recorder.onstop = async () => {
      const blob = new Blob(chunks, { type: 'audio/webm' });
      const fd = new FormData();
      fd.append('file', blob, 'question.webm');
      const h = { ...getAuthHeaders() };
      try { delete h['Content-Type']; delete h['content-type']; } catch {}
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/assistant/transcribe`, { method: 'POST', headers: h, body: fd });
      const data = await res.json();
      const t = (data && data.text) ? String(data.text).trim() : '';
      if (t) {
        setQuestion(t);
        ask(t, { is_voice: true });
      }
      try { stream.getTracks().forEach(tk => tk.stop()); } catch {}
      setRecState({ recording: false, supported: true });
    };
    recorder.start();
    setRecState({ recording: true, supported: true });
    setTimeout(() => { try { recorder.stop(); } catch {} }, 6000);
  };

  const ask = async (overrideText, opts = {}) => {
    const qUse = (overrideText !== undefined ? overrideText : question).trim();
    if (!qUse) return;
    setLoading(true); setAnswer(''); setDebug(null);
    try {
      // try {
      //   const dbgRes = await fetch(`${API_BASE_URL}/api/v4/documents/assistant/query`, {
      //     method: 'POST',
      //     headers,
      //     body: JSON.stringify({ question: qUse, file_ids: selectedIds, top_k: 8, debug_only: true })
      //   });
      //   if (dbgRes.ok) {
      //     const dbg = await dbgRes.json();
      //     setDebug(dbg?.debug || null);
      //   }
      // } catch {}

      const ctrl = new AbortController();
      setAbortCtrl(ctrl);
      const tAnswerStart = Date.now();
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/assistant/query/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ question: qUse, file_ids: selectedIds, top_k: 8, is_voice: !!opts.is_voice }),
        signal: ctrl.signal,
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        acc += chunk;
        setAnswer(prev => (prev || '') + chunk);
      }
      setHistory(prev => [...prev, { q: qUse, a: acc }]);
      setQuestion('');
      // After streaming completes, re-fetch debug-only snapshot to capture all earlier steps
      // then append the final completion time so totals include the last step
      const dt = Date.now() - tAnswerStart;
      try {
        const dbgRes2 = await fetch(`${API_BASE_URL}/api/v4/documents/assistant/query`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ question: qUse, file_ids: selectedIds, top_k: 8, debug_only: true })
        });
        if (dbgRes2.ok) {
          const dbg2 = await dbgRes2.json();
          const base = (dbg2 && dbg2.debug) ? dbg2.debug : {};
          const steps = Array.isArray(base.steps) ? [...base.steps] : [];
          steps.push({ name: 'answer_completion', ms: dt });
          setDebug({ ...base, steps });
        } else {
          // Fallback: append to existing
          setDebug(prev => {
            const base = prev && typeof prev === 'object' ? prev : {};
            const steps = Array.isArray(base.steps) ? [...base.steps] : [];
            steps.push({ name: 'answer_completion', ms: dt });
            return { ...base, steps };
          });
        }
      } catch {
        setDebug(prev => {
          const base = prev && typeof prev === 'object' ? prev : {};
          const steps = Array.isArray(base.steps) ? [...base.steps] : [];
          steps.push({ name: 'answer_completion', ms: dt });
          return { ...base, steps };
        });
      }
    } catch (e) {
      setAnswer(`Error: ${String(e)}`);
    }
    setLoading(false);
    setAbortCtrl(null);
    setRecState((s) => ({ ...s, recording: false }));
  };

  const stop = () => {
    try { abortCtrl && abortCtrl.abort(); } catch (_) {}
    setAbortCtrl(null);
  };

  const canStop = speaking || (typeof window !== 'undefined' && window.speechSynthesis && (window.speechSynthesis.speaking || window.speechSynthesis.pending));

  return (
    <div className="container py-4">
      <h3 className="mb-3">Documents v4</h3>
      <V4Tabs />
      <div className="row g-3">
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-body d-flex flex-column">
              <div className="d-flex mb-2">
                <input className="form-control" placeholder="Filter documents" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} />
                <button className="btn btn-outline-secondary ms-2" onClick={() => { setOffset(0); loadDocs(); }}>Go</button>
              </div>
              <div className="flex-grow-1 overflow-auto border rounded p-2" style={{ minHeight: 240 }}>
                {(docs || []).map((d) => (
                  <label key={d.id} className="d-flex align-items-center gap-2 py-1">
                    <input type="checkbox" checked={selectedIds.includes(d.id)} onChange={() => toggleSelect(d.id)} />
                    <span className="small" title={d.original_filename}>{d.original_filename}</span>
                  </label>
                ))}
                {(!docs || docs.length === 0) && <div className="text-muted small">No documents</div>}
              </div>
              <div className="d-flex justify-content-between align-items-center mt-2">
                <button className="btn btn-outline-secondary btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Prev</button>
                <div className="small text-muted">{offset + 1}-{Math.min(offset + limit, total)} / {total}</div>
                <button className="btn btn-outline-secondary btn-sm" disabled={(offset + limit) >= total} onClick={() => setOffset(offset + limit)}>Next</button>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-8">
          <div className="card h-100">
            <div className="card-body d-flex flex-column">
              <div className="mb-2 d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <textarea className="form-control" rows={5} placeholder="Type your question... or use the mic" value={question} onChange={(e) => setQuestion(e.target.value)} />
                </div>
                <div className="d-flex flex-column gap-2">
                  <button className="btn btn-outline-secondary" disabled={!srState.supported || srState.listening} onClick={startLiveSTT} title="Live speech to text">
                    {srState.listening ? 'Listening…' : '🎙️ Live'}
                  </button>
                  <button className="btn btn-outline-secondary" disabled={!recState.supported || recState.recording || srState.listening} onClick={startRecording} title="Record up to 6 seconds (fallback)">
                    {recState.recording ? 'Recording…' : '🎤 Mic'}
                  </button>
                  {srState.listening && (
                    <button className="btn btn-outline-danger" onClick={stopLiveSTT}>Stop</button>
                  )}
                </div>
              </div>
              <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                <button className="btn btn-primary" disabled={loading || !question.trim()} onClick={() => ask()}>{loading ? 'Asking…' : 'Ask'}</button>
                <button className="btn btn-outline-danger" disabled={!abortCtrl} onClick={stop}>Stop</button>
                <div className="form-check form-switch ms-auto">
                  <input className="form-check-input" type="checkbox" id="ttsToggle" checked={ttsEnabled} onChange={(e) => setTtsEnabled(e.target.checked)} />
                  <label className="form-check-label" htmlFor="ttsToggle">Read answers</label>
                </div>
                <button className="btn btn-outline-secondary" disabled={!answer} onClick={() => speakText(answer)}>🔊 Read now</button>
                <button className="btn btn-outline-secondary" disabled={!canStop} onClick={stopSpeaking}>⏹ Stop</button>
                <div className="ms-2 btn-group" role="group" aria-label="View mode">
                  <button type="button" className={`btn btn-sm ${viewMode==='beautified' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setViewMode('beautified')}>Beautified</button>
                  <button type="button" className={`btn btn-sm ${viewMode==='raw' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setViewMode('raw')}>Raw</button>
                </div>
              </div>
              <div className="mt-3 border rounded p-2 flex-grow-1" style={{ overflow: 'auto' }}>
                {answer ? (
                  viewMode === 'raw' ? (
                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{answer}</pre>
                  ) : (
                    <div style={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
                      <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: ({node, ...props}) => <h1 style={{ fontSize: '1.4rem', marginTop: '0.75rem' }} {...props} />,
                        h2: ({node, ...props}) => <h2 style={{ fontSize: '1.2rem', marginTop: '0.75rem' }} {...props} />,
                        h3: ({node, ...props}) => <h3 style={{ fontSize: '0.95rem', marginTop: '0.5rem' }} {...props} />,
                        h4: ({node, ...props}) => <h4 style={{ fontSize: '0.9rem', marginTop: '0.5rem' }} {...props} />,
                        img: ({node, ...props}) => {
                          try {
                            const src = props.src || '';
                            const isAbsolute = /^https?:\/\//i.test(src) || src.startsWith('/uploads');
                            const resolved = isAbsolute ? src : `${API_BASE_URL}${src}`;
                            return <img alt={props.alt || ''} src={resolved} style={{maxWidth:'100%'}} />
                          } catch (_) {
                            return <img alt={props.alt || ''} {...props} style={{maxWidth:'100%'}} />
                          }
                        }
                      }}
                      >{answer}</ReactMarkdown>
                    </div>
                  )
                ) : (
                  <span className="text-muted">The assistant will reply here.</span>
                )}
              </div>
              {debug && (
                <div className="mt-3">
                  <div className="accordion" id="assistDebug">
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingDbgA">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseDbgA" aria-expanded="false" aria-controls="collapseDbgA">
                          Debug (steps)
                        </button>
                      </h2>
                      <div id="collapseDbgA" className="accordion-collapse collapse" aria-labelledby="headingDbgA" data-bs-parent="#assistDebug">
                        <div className="accordion-body">
                          <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowY: 'auto', overflowX: 'hidden', maxHeight: '50vh' }}>{JSON.stringify(debug, null, 2)}</pre>
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingDbgTiming">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseDbgTiming" aria-expanded="false" aria-controls="collapseDbgTiming">
                          Timing (ms)
                        </button>
                      </h2>
                      <div id="collapseDbgTiming" className="accordion-collapse collapse" aria-labelledby="headingDbgTiming" data-bs-parent="#assistDebug">
                        <div className="accordion-body">
                          {Array.isArray(debug?.steps) && debug.steps.length > 0 ? (
                            <div className="table-responsive">
                              <table className="table table-sm table-striped align-middle mb-3">
                                <thead>
                                  <tr>
                                    <th style={{width:'3rem'}}>#</th>
                                    <th>Step</th>
                                    <th>Variant</th>
                                    <th style={{width:'8rem'}}>Time (ms)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {debug.steps.map((s, i) => (
                                    <tr key={i}>
                                      <td className="text-muted">{i + 1}</td>
                                      <td><code className="small">{String(s?.name || '')}</code></td>
                                      <td className="small text-muted">{s?.response?.variant || s?.variant || ''}</td>
                                      <td>{typeof s?.ms === 'number' ? s.ms : ''}</td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot>
                                  <tr>
                                    <th colSpan={2} className="text-end">Total</th>
                                    <th>{debug.steps.reduce((acc, s) => acc + (typeof s?.ms === 'number' ? s.ms : 0), 0)}</th>
                                  </tr>
                                </tfoot>
                              </table>
                              {/* Aggregates per step */}
                              {(() => {
                                const agg = {};
                                for (const s of (debug.steps || [])) {
                                  const k = String(s?.name || '');
                                  if (!k) continue;
                                  if (!agg[k]) agg[k] = { count: 0, sum: 0 };
                                  if (typeof s?.ms === 'number') {
                                    agg[k].count += 1;
                                    agg[k].sum += s.ms;
                                  }
                                }
                                const rows = Object.entries(agg);
                                if (!rows.length) return null;
                                return (
                                  <table className="table table-sm table-bordered align-middle">
                                    <thead>
                                      <tr>
                                        <th>Step</th>
                                        <th className="text-end">Count</th>
                                        <th className="text-end">Total (ms)</th>
                                        <th className="text-end">Avg (ms)</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {rows.map(([name, v]) => (
                                        <tr key={name}>
                                          <td><code className="small">{name}</code></td>
                                          <td className="text-end">{v.count}</td>
                                          <td className="text-end">{v.sum}</td>
                                          <td className="text-end">{v.count ? Math.round(v.sum / v.count) : 0}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                );
                              })()}
                            </div>
                          ) : (
                            <div className="text-muted small">No timing data available.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsAssistantV4;


